import { useEffect, useMemo, useRef, useState } from "react";
import {
  getItemValidations,
  getItemValue,
  getName,
} from "@shared/features/configuration/utils";
import { getNormalizedPercentages } from "@shared/features/trades/common/utils";
import { toFixed } from "@shared/utils/numbers";
import { findLastNonZeroValue } from "@shared/utils/arrays";
import {
  BotCustomStrategyTargetsData,
  CustomStrategyError,
  CustomStrategyErrorData,
} from "@shared/features/bots/BotForm/common/types";
import { isSymbol } from "@shared/types/utils";
import { DEFAULT_STRATEGY_TARGETS } from "@shared/features/bots/BotForm/common/constants";
import { ConfigurationItem, CustomStrategyData } from "@shared/types";

interface useSharedCustomStrategyFormProps {
  customStrategyItem: ConfigurationItem | undefined;
  addNumTargetsSelect?: boolean;
  includeNameSelect?: boolean;
  customStrategies?: ConfigurationItem[];
  shouldInitializeTargets?: boolean;
}

const useSharedCustomStrategyForm = ({
  customStrategyItem,
  addNumTargetsSelect,
  includeNameSelect,
  customStrategies,
  shouldInitializeTargets = true,
}: useSharedCustomStrategyFormProps) => {
  const itemName = getName(customStrategyItem);
  const itemValue = getItemValue(customStrategyItem);
  const [targets, setTargets] = useState<CustomStrategyData["targets"]>();
  const [showNumTargetsSelect, setShowNumTargetsSelect] = useState(false);
  const parsedTargets = useMemo(() => {
    return isSymbol(targets) ? DEFAULT_STRATEGY_TARGETS : targets;
  }, [targets]);
  const [numStrategyTargets, setNumStrategyTargets] = useState<
    number | undefined
  >();
  const [strategyModified, setStrategyModified] = useState(false);
  const [updatedName, setUpdatedName] = useState<string>("");
  const inputRef = useRef<any>(null);
  const [errors, setErrors] = useState<CustomStrategyErrorData>({
    [CustomStrategyError.TARGET]: {},
    [CustomStrategyError.TOTAL_TARGETS]: undefined,
    [CustomStrategyError.STRATEGY_NAME]: undefined,
  });

  const {
    max_objects: maxObjects,
    max_length: maxLength,
    max,
    min,
  } = getItemValidations(customStrategyItem) || {};
  const { targets: itemTargets } = itemValue || {};

  const normalizeTargets = (targets: (number | string)[]) => {
    return getNormalizedPercentages(
      targets.map((target) => Number(target)),
      2
    ).map((target) => toFixed(target, 2));
  };

  const initializeValues = (targets?: CustomStrategyData["targets"]) => {
    if (targets && shouldInitializeTargets) {
      if (!isSymbol(targets)) {
        let lastNonZeroValueIndex = findLastNonZeroValue(targets);
        if (addNumTargetsSelect && lastNonZeroValueIndex === targets.length) {
          lastNonZeroValueIndex = 4;
          setShowNumTargetsSelect(true);
        } else if (
          !addNumTargetsSelect ||
          lastNonZeroValueIndex !== targets.length
        ) {
          setShowNumTargetsSelect(false);
        }
        setNumStrategyTargets(lastNonZeroValueIndex);
        setTargets(normalizeTargets(targets.slice(0, lastNonZeroValueIndex)));
      } else {
        setTargets(targets);
      }
      setUpdatedName(itemName || "");
      setStrategyModified(false);
    }
  };

  useEffect(() => {
    if (itemTargets && numStrategyTargets && addNumTargetsSelect) {
      const updatedTargets = itemTargets.slice(0, numStrategyTargets);

      setTargets(normalizeTargets(updatedTargets));
      setStrategyModified(false);
    }
  }, [numStrategyTargets]);

  useEffect(() => {
    inputRef.current && inputRef.current.focus();
  }, [inputRef]);

  const setTargetError = (targetIndex: number, error?: string) => {
    if (error !== errors[CustomStrategyError.TARGET][targetIndex]) {
      setErrors((errors) => ({
        ...errors,
        [CustomStrategyError.TARGET]: {
          ...errors[CustomStrategyError.TARGET],
          [targetIndex]: error,
        },
      }));
    }
  };

  const setNameError = (error?: string) => {
    if (error !== errors[CustomStrategyError.STRATEGY_NAME]) {
      setErrors((errors) => ({
        ...errors,
        [CustomStrategyError.STRATEGY_NAME]: error,
      }));
    }
  };

  const setTotalTargetsError = (error?: string) => {
    if (error !== errors[CustomStrategyError.TOTAL_TARGETS]) {
      setErrors((errors) => ({
        ...errors,
        [CustomStrategyError.TOTAL_TARGETS]: error,
      }));
    }
  };

  const customStrategiesNames = useMemo(
    () =>
      customStrategies
        ?.filter(
          (strategy) => getName(strategy) !== getName(customStrategyItem)
        )
        ?.map((strategy) => getName(strategy)),
    [customStrategies]
  );

  const validateName = () => {
    let error;
    if (updatedName && updatedName.length > maxLength) {
      error = `Strategy name should have less than ${maxLength} characters`;
    } else if (!updatedName) {
      error = "Please choose a strategy name";
    } else if (customStrategiesNames?.includes(updatedName)) {
      error = "This strategy name already exists";
    }
    setNameError(error);
    return !error;
  };

  const targetsIsMultiValue = isSymbol(targets);
  const targetsSum = toFixed(
    parsedTargets?.reduce((sum: number, target) => {
      sum += Number(target) || 0;
      return sum;
    }, 0) || 0,
    2
  );

  const validateTargets = () => {
    let error = false;
    if (!targetsIsMultiValue) {
      parsedTargets?.forEach((target, index) => {
        let targetError;
        if (target > max || target < min) {
          targetError = `Target ratio should be between ${min} and ${max}`;
        }
        error = error || !!targetError;
        setTargetError(index, targetError);
      });
      if (targetsSum !== 100) {
        setTotalTargetsError("The sum of the targets ratios must be 100%");
        error = true;
      } else {
        setTotalTargetsError(undefined);
      }
    } else {
      setTotalTargetsError(undefined);
    }
    return !error;
  };

  const updateTarget = (targetIndex: number, value: number) => {
    setTargets(Object.assign([], targets, { [targetIndex]: value }));
    setStrategyModified(true);
  };

  const removeTargetAtPosition = (targetIndex: number) => {
    if (targetsIsMultiValue) return;
    const targetsBeforeRemoved = targets?.slice(0, targetIndex) || [];
    const targetsAfterRemoved = targets
      ? targets.slice(targetIndex + 1, targets.length)
      : [];
    return [...targetsBeforeRemoved, ...targetsAfterRemoved];
  };

  const removeTarget = (targetIndex?: number) => {
    if (!targets || targetsIsMultiValue) return;
    const targetNumber =
      targetIndex === undefined ? targets.length - 1 : targetIndex;
    if (targetNumber >= 0) {
      setTargets(removeTargetAtPosition(targetNumber));
    }
    setStrategyModified(true);
  };

  const addTarget = () => {
    if (
      targets &&
      !targetsIsMultiValue &&
      (!maxObjects || maxObjects > targets.length)
    ) {
      setTargets([...targets, 0]);
      setStrategyModified(true);
    }
  };

  const validateStrategy = () => {
    const nameValid = !includeNameSelect || validateName();
    const targetsValid = validateTargets();
    return targetsIsMultiValue || (nameValid && targetsValid);
  };

  const nameError = errors[CustomStrategyError.STRATEGY_NAME];

  const nameInputData = {
    value: updatedName,
    onChange: setUpdatedName,
    ref: inputRef,
    error: nameError,
  };

  const numTargets = parsedTargets?.length || 0;
  const targetsWithDefault: CustomStrategyData["targets"] = useMemo(() => {
    return targets || [];
  }, [targets]);

  const targetsData: BotCustomStrategyTargetsData = {
    removeTarget,
    addTarget,
    updateTarget,
    targets: targetsWithDefault,
    parsedTargets,
    disabledAdd: numTargets === maxObjects,
    disabledRemove: numTargets <= 1,
    numTargets,
    targetsSum,
    showOverrideButton: targetsIsMultiValue,
    onOverridePress: () => {
      initializeValues(DEFAULT_STRATEGY_TARGETS);
    },
    setTargets,
  };

  const numTargetsSelectorData = {
    shouldDisplay: showNumTargetsSelect,
    opacity: strategyModified ? 0.3 : 1,
    value: numStrategyTargets,
    onChange: setNumStrategyTargets,
    options: [...Array(itemTargets?.length || 1).keys()].map((index) => {
      const targetNumber = index + 1;
      return {
        value: targetNumber,
        label: `${targetNumber} Target${targetNumber === 1 ? "" : "s"}`,
      };
    }),
  };

  return {
    errors,
    nameInputData,
    targetsData,
    numTargetsSelectorData,
    isValid: validateStrategy(),
    initializeValues,
    strategyModified,
  };
};

export default useSharedCustomStrategyForm;
